import { createAsync, query, useSearchParams } from "@solidjs/router";
import { Accessor, createMemo, Match, Show, Suspense, Switch } from "solid-js";
import { getHomeV2RouteData, HomeV2RouteData } from "./data/home_v2_route_data";
import { axisFloorBgDesktop, axisFloorBgMobile } from "~/assets/assets";
import {
  BrandsListForDesktop,
  BrandsListForMobile,
  SingleBrandCard,
} from "./brands_card";
import { GetGiftCardText, Name } from "./components";
import { Spacer } from "~/widgets/spacer";
import { Product } from "~/server/types/search";
import { HomeV2Provider } from "./home_v2_context";
import { useWebStoreContext } from "~/context/webstore_context";
import { BottomBranding$Client } from "~/components/partner_components/partner_bottom_page_branding";
import { DottedLoader } from "~/widgets/loader";
import { clientOnly } from "@solidjs/start";

const getHomeV2RouteData$C = query(getHomeV2RouteData, "axis_home_page");

// client only
export default clientOnly(async () => ({ default: AxisHomePage }), {
  lazy: true,
});

export function AxisHomePage() {
  const routeData: Accessor<HomeV2RouteData | undefined> = createAsync(
    () => getHomeV2RouteData$C(),
    { deferStream: true }
  );

  return (
    <Suspense fallback={<DottedLoader />}>
      <Show when={routeData()}>
        <HomeComponentV2Content
          routeData={routeData as Accessor<HomeV2RouteData>}
        />
      </Show>
    </Suspense>
  );
}

function HomeComponentV2Content(props: {
  routeData: Accessor<HomeV2RouteData>;
}) {
  const { storeState, getClientId } = useWebStoreContext();

  const [queryParams, _setQueryParams] = useSearchParams();
  let maxBrands = parseInt((queryParams.maxBrands as string) ?? "0");

  const allBrands: Accessor<Product[]> = createMemo(() => {
    const brands = props.routeData().brands;

    if (maxBrands > 0) {
      return brands.slice(0, maxBrands);
    }

    return brands;
  });

  return (
    <HomeV2Provider>
      <div class="relative flex h-max  flex-col items-center justify-start gap-4">
        <div
          class="hidden sm:block"
          classList={{
            "sm:mt-[16px]": allBrands().length == 1,
            "sm:mt-[40px]": allBrands().length > 1,
          }}
        ></div>
        <div class="hidden w-full flex-col items-center justify-start sm:flex">
          <DesktopUI />
        </div>
        <div class="flex w-full flex-col items-center justify-start md:hidden">
          <MobileUI />
        </div>
        <div class="mt-[100px]">&nbsp;</div>
        <BottomBranding$Client clientId={getClientId} />

        <Show when={allBrands().length == 1}>
          <div
            class={`fixed bottom-[70px] flex h-[200px] w-full bg-[image:var(--mobile-image-url)] bg-cover bg-center sm:bottom-[50px] sm:h-[338px] sm:bg-[image:var(--desktop-image-url)]`}
            style={{
              "background-size": "100% 100%",
              "--mobile-image-url": `url(${axisFloorBgMobile})`,
              "--desktop-image-url": `url(${axisFloorBgDesktop})`,
            }}
          ></div>
        </Show>
      </div>
    </HomeV2Provider>
  );

  function MobileUI() {
    return (
      <>
        <div class="w-full px-3">
          <div class="flex w-full flex-col items-center justify-start rounded-b-3xl bg-cover py-8 ">
            <Name
              name={storeState.v4HomePage?.user?.firstName ?? ""}
              clientId={getClientId()}
            />
            <Spacer height={8} />
            <span class="max-w-[319px] text-center font-jakartaSans text-[24px] font-medium leading-[32px] tracking-[-0.48px] md:font-semibold md:leading-[36px] md:tracking-normal">
              <HeaderText />
            </span>
          </div>
        </div>
        <Spacer height={24} />
        <Show when={allBrands().length > 1}>
          <GetGiftCardText />
          <Spacer height={20} />
        </Show>
        <Switch>
          <Match when={allBrands().length > 1}>
            <BrandsListForMobile
              allBrands={allBrands}
              clientId={getClientId()}
            />
          </Match>
          <Match when={allBrands().length == 1}>
            <SingleBrandCard brand={() => allBrands()[0]} />
          </Match>
        </Switch>
      </>
    );
  }

  function DesktopUI() {
    return (
      <>
        <Name
          name={storeState.v4HomePage?.user?.firstName ?? ""}
          clientId={getClientId()}
        />
        <Spacer height={8} />

        <span
          class="z-10 text-center font-jakartaSans text-[48px]"
          classList={{
            "max-w-[670px] font-semibold": true,
          }}
        >
          <HeaderText />
        </span>
        <Show when={allBrands().length > 1}>
          <div class="mt-[44px]"></div>
          <GetGiftCardText />
        </Show>
        <div class="mt-8"></div>
        <Switch>
          <Match when={allBrands().length > 1}>
            {BrandsListForDesktop({
              allBrands: allBrands(),
            })}
          </Match>
          <Match when={allBrands().length == 1}>
            {SingleBrandCard({
              brand: () => allBrands()[0],
            })}
          </Match>
        </Switch>
      </>
    );
  }

  function HeaderText() {
    return <span class="block">Claim your reward</span>;
  }
}
